// Chip — generic chip primitive. State variants consolidate the
// duplicated chip styles across .dose-chip, HALT toggle pills, trigger
// chips, ACT exercise tags, motivational toast chips.
//
// Use the `as` prop to render as a button (interactive) or a span
// (decorative). Defaults to span so static lists don't accidentally
// become buttons.

import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Icon } from './Icon';
import { cn } from '../../lib/cn';

type State = 'default' | 'active' | 'warning' | 'success';

interface BaseChipProps {
  state?: State;
  icon?: LucideIcon;
  children: ReactNode;
  className?: string;
}

interface SpanChipProps extends BaseChipProps, Omit<HTMLAttributes<HTMLSpanElement>, 'className' | 'children'> {
  as?: 'span';
}

interface ButtonChipProps extends BaseChipProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> {
  as: 'button';
}

type ChipProps = SpanChipProps | ButtonChipProps;

function chipClass(state: State, extra?: string) {
  return cn(
    'chip',
    state === 'active' && 'chip-active',
    state === 'warning' && 'chip-warning',
    state === 'success' && 'chip-success',
    extra,
  );
}

export function Chip(props: ChipProps) {
  const { state = 'default', icon, children, className, ...rest } = props;
  const cls = chipClass(state, className);
  const body = (
    <>
      {icon ? <Icon icon={icon} size={14} /> : null}
      <span>{children}</span>
    </>
  );
  if (props.as === 'button') {
    const { as: _as, ...buttonRest } = rest as ButtonChipProps;
    void _as;
    return (
      <button type="button" className={cls} {...buttonRest}>
        {body}
      </button>
    );
  }
  const { as: _as, ...spanRest } = rest as SpanChipProps;
  void _as;
  return (
    <span className={cls} {...spanRest}>
      {body}
    </span>
  );
}
